import { css, html, LitElement, type TemplateResult } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { SidenavItem } from './types'

@safeCustomElement('mdc-sidenav')
export class MdcSidenav extends LitElement {
  @property({ type: Boolean, attribute: 'is-open' }) isOpen = true
  @property({ type: String }) title = ''
  @property({ type: Array }) items: SidenavItem[] = []

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-sidenav {
			width: 100%;
			height: 100%;
			display: flex;
			flex-direction: column;
			background-color: var(--color-background-primary, var(--color-functional-gray-white));
			position: relative;
			box-sizing: border-box;
		}

		.mdc-sidenav__header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0 var(--spacing-100, 1rem);
			border-bottom: 1px solid var(--color-border-primary, var(--color-functional-gray-75));
		}

		.mdc-sidenav__title {
			font-size: var(--text-112-and-half, 1.125rem);
			color: var(--color-foreground-primary, var(--color-functional-gray-black)) !important;
			font-weight: 600;
			line-height: 1.2;
			letter-spacing: -0.01em;
			margin: revert;
			padding: revert;
		}

		.mdc-sidenav__close {
			display: none;
			align-items: center;
			justify-content: center;
			width: 2.5rem;
			height: 2.5rem;
			color: var(--color-foreground-secondary, var(--color-functional-gray-400));
			transition: color 0.2s ease-in-out;
			border: none;
			background: none;
			cursor: pointer;
			padding: 0;
		}

		.mdc-sidenav__close:hover {
			color: var(--color-foreground-primary, var(--color-functional-gray-black));
		}

		.mdc-sidenav__close:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		.mdc-sidenav__content {
			flex: 1;
			padding: var(--spacing-100, 1rem);
			overflow-y: auto;
		}

		.mdc-sidenav__nav {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-025, 0.25rem);
			padding-bottom: 12.5rem;
		}

		.mdc-sidenav__nav-item {
			display: flex;
			align-items: center;
			padding: var(--spacing-050, 0.5rem) var(--spacing-100, 1rem) !important;
			color: var(--color-foreground-interactive-primary-default, var(--color-functional-gray-black)) !important;
			font-size: var(--typography-font-size-087-and-half, 0.875rem) !important;
			font-weight: 450 !important;
			border-radius: var(--radius-md, 0.5rem);
			transition: background-color 0.2s ease-in-out;
			text-decoration: none;
			width: 100%;
			text-align: left;
			box-sizing: border-box;
			gap: var(--spacing-075, 0.75rem);
		}

		.mdc-sidenav__nav-item:hover {
			background-color: var(--color-background-Interactive-secondary-default, var(--color-functional-gray-25)) !important;
		}

		.mdc-sidenav__nav-item:focus-visible {
			box-shadow: 0 0 0 2px var(--color-functional-blue-300);
			outline: none;
		}

		.mdc-sidenav__nav-item--active {
			background-color: var(--color-background-Interactive-secondary-default, var(--color-functional-gray-25));
			color: var(--color-foreground-interactive-active, var(--color-functional-gray-black));
		}

		.mdc-sidenav__nav > .mdc-sidenav__nav-item {
			font-weight: 500;
			text-transform: uppercase;
		}

		.mdc-sidenav__nav-item svg {
			width: 1rem;
			height: 1rem;
			flex-shrink: 0;
		}

		.mdc-sidenav__nav-children {
			margin-left: var(--spacing-200, 2rem);
			display: flex;
			flex-direction: column;
			gap: var(--spacing-012-and-half, 0.125rem);
		}

		/* breakpoints.lg - 1 */
		@media (max-width: 1023px) {
			.mdc-sidenav {
				z-index: 1000;
			}

			.mdc-sidenav--collapsed {
				width: 0;
			}

			.mdc-sidenav__close {
				display: flex;
				margin: revert !important;
				padding: revert !important;
			}
		}
	`

  private renderSidenavItem(item: SidenavItem, _index: number): TemplateResult {
    const hasChildren = item.items && item.items.length > 0
    const classes = [
      'mdc-sidenav__nav-item',
      item.isActive ? 'mdc-sidenav__nav-item--active' : '',
      hasChildren ? 'mdc-sidenav__nav-item--has-children' : '',
    ]
      .filter(Boolean)
      .join(' ')

    if (!item.href) {
      return html``
    }

    return html`
			<a href=${item.href} class=${classes}>${item.label}</a>
			${
        hasChildren
          ? html`
						<div class="mdc-sidenav__nav-children">
							${item.items?.map((child, childIndex) => this.renderSidenavItem(child, childIndex))}
						</div>
					`
          : ''
      }
		`
  }

  render() {
    const className = `mdc-sidenav${this.isOpen ? ' mdc-sidenav--open' : ''}`

    return html`
			<div class=${className}>
				<div class="mdc-sidenav__header">
					<h2 class="mdc-sidenav__title">${this.title}</h2>
					<button type="button" class="mdc-sidenav__close" aria-label="Close navigation">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
							<path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
						</svg>
					</button>
				</div>
				<div class="mdc-sidenav__content">
					<nav class="mdc-sidenav__nav">
						${this.items.map((item, index) => this.renderSidenavItem(item, index))}
					</nav>
				</div>
			</div>
		`
  }
}
